import {
  csmQuotaMetrics,
  csmQuotaTypes,
  csmRenewalValues,
  jobsCsmSelectableQuotaPeriods,
  jobsSalesCycles,
} from '@/components/partials/jobs/constants/selectableOptions';
import * as utils from '@/components/partials/jobs/JobsInquiryForm/utils/common';
import * as csmUtils from '@/components/partials/jobs/JobsInquiryForm/utils/csmRoleUtils';
import {
  getQuotaAttainmentPerLastYearFormField,
  getQuotaAttainmentPerPeriodFormField,
} from '@/components/partials/jobs/JobsInquiryForm/utils/formFields';
import type {
  JobsFormField,
  JobsSectionModels,
} from '@/components/partials/public-portfolio/PortfolioJobsInquirySection/PortfolioJobsInquirySectionProfile/types';
import { JobsInquiryCsmQuota } from '@/types/graphql';

export default ({ inquiry }: JobsSectionModels): JobsFormField[] => {
  return [
    {
      attributeName: 'salesCycleLength',
      type: 'BravadoSelectV3',
      getAttrs() {
        return {
          label: 'How long does it take you to renew or up-sell a client from start to finish?',
          options: jobsSalesCycles.map(utils.mapOnboardingOptionsToSelectInput),
        };
      },
      getIsHidden(form) {
        return !csmUtils.hasRenewalsOrUpselsInQuotaMeasuring({
          csmQuotaTypeMain: form.csmQuotaTypeMain,
          csmQuotaMetricMain: form.csmQuotaMetricMain as string[],
        });
      },
    },

    {
      attributeName: 'csmQuotaTypeMain',
      attributeNameOther: 'csmQuotaTypeOther',
      type: 'JobsSelectWithCustomOption',
      getAttrs() {
        return {
          label: 'How is quota structured?',
          options: utils.mapJobsOnboardingSelectOptionsToBravadoSelectOptions(csmQuotaTypes),
          mainValue: inquiry.csmQuotaTypeMain,
          otherValue: inquiry.csmQuotaTypeOther,
        };
      },
    },

    {
      attributeName: 'csmQuotaMetricMain',
      attributeNameOther: 'csmQuotaMetricOther',
      type: 'JobsMultiselectWithCustomOption',
      getAttrs() {
        return {
          label: 'How is your quota measured?',
          options: utils.mapJobsOnboardingSelectOptionsToBravadoSelectOptions(csmQuotaMetrics),
          multi: true,
          mainValue: inquiry.csmQuotaMetricMain,
          otherValue: inquiry.csmQuotaMetricOther,
        };
      },
      getIsHidden(form) {
        return csmUtils.getIsEditModalQuotaFieldIsHidden(form);
      },
    },

    {
      attributeName: 'csmQuotaPeriodMain',
      attributeNameOther: 'csmQuotaPeriodOther',
      type: 'JobsSelectWithCustomOption',
      getAttrs() {
        return {
          label: 'What is your quota period?',
          options: utils.mapJobsOnboardingSelectOptionsToBravadoSelectOptions(jobsCsmSelectableQuotaPeriods),
          mainValue: inquiry.csmQuotaPeriodMain,
          otherValue: inquiry.csmQuotaPeriodOther,
          dropdownDirection: 'top',
        };
      },
      getIsHidden(form) {
        return csmUtils.getIsEditModalQuotaFieldIsHidden(form);
      },
    },

    {
      ...getQuotaAttainmentPerPeriodFormField(),
      getIsHidden(form) {
        return (
          csmUtils.getIsEditModalQuotaFieldIsHidden(form) || csmUtils.getIsQoutaAttainmentLastYearFieldIsHidden(form)
        );
      },
    },

    {
      ...getQuotaAttainmentPerLastYearFormField(),
      getIsHidden(form) {
        return csmUtils.getIsEditModalQuotaFieldIsHidden(form);
      },
    },

    {
      attributeName: 'csmRenewalValue',
      type: 'BravadoSelectV3',
      getAttrs() {
        return {
          label: 'What is your average contract value for renewals?',
          options: utils.mapJobsOnboardingSelectOptionsToBravadoSelectOptions(csmRenewalValues),
        };
      },
      getIsHidden(form) {
        if (form.csmQuotaTypeMain === JobsInquiryCsmQuota.CsmNoQuota) return true;

        return !csmUtils.hasRenewalsInQuotaMeasuring(form.csmQuotaMetricMain as string[]);
      },
    },

    {
      attributeName: 'csmUpsellValue',
      type: 'BravadoSelectV3',
      getAttrs() {
        return {
          label: 'What is your average contract value for up-sells?',
          options: utils.mapJobsOnboardingSelectOptionsToBravadoSelectOptions(csmRenewalValues),
          dropdownDirection: 'top',
        };
      },
      getIsHidden(form) {
        return !csmUtils.hasUpselsInQuotaMeasuring({
          csmQuotaTypeMain: form.csmQuotaTypeMain,
          csmQuotaMetricMain: form.csmQuotaMetricMain as string[],
        });
      },
    },
  ];
};
